import { FocusMonitor } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    FormGroupDirective,
    FormsModule,
    NgControl,
    NgForm,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
    Validators,
} from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { Subject, take } from 'rxjs';

export class CustomErrorMatcher implements ErrorStateMatcher {
    isErrorState(control: AbstractControl | null, form: FormGroupDirective | NgForm | null): boolean {
        //console.log('isErrorState', control, control.invalid);
        return control.invalid;
    }
}
@Component({
    selector: 'app-time-duration-input',
    templateUrl: './time-duration-input.component.html',
    styleUrls: ['./time-duration-input.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatInputModule, CommonModule],
    providers: [
        {
            provide: MatFormFieldControl,
            useExisting: TimeDurationInputComponent,
        },
        {
            provide: ErrorStateMatcher,
            useClass: CustomErrorMatcher,
        },
        // {
        //     provide: NG_VALIDATORS,
        //     useExisting: TimeDurationInputComponent,
        //     multi: true,
        // },
    ],
})
export class TimeDurationInputComponent implements OnInit, OnDestroy, MatFormFieldControl<number>, ControlValueAccessor, Validator {
    static nextId = 0;

    form: FormGroup;

    timePattern = /^(?:(\d+s)?\s*(\d+j)?\s*(\d+h)?\s*(\d+m)?\s?)$/;

    formatedTime: string = '';

    @ViewChild(MatInput, { read: ElementRef, static: true })
    input: ElementRef;

    /**
     * Définit la valeur du contrôle.
     */
    @Input()
    set value(value: number) {
        this.formatedTime = this.formatTime(value);
        this.form.patchValue({ timeInput: this.formatedTime });
        // Previent le formulaire que le composant a été modifié pour declencher le change detection.
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du contrôle.
     */
    get value(): number {
        const time = this.parseTimeInput(this.form.get('timeInput').value);
        return time;
    }

    /**
     * Emit lorsque des changements surviennent dans le composant pour prevenir le MatFormField parent de déclencher le change detection.
     */
    stateChanges = new Subject<void>();

    /**
     * Id du contrôle.
     */
    @HostBinding()
    id: string = `mat-form-field-custom-control-${TimeDurationInputComponent.nextId++}`;

    private _placeholder: string;

    /**
     * Définit la valeur du placeholder.
     */
    @Input()
    set placeholder(value: string) {
        this._placeholder = value;
        // Previent le formulaire que le composant a été modifié pour declencher le change detection.
        this.stateChanges.next();
    }

    /**
     * Obtient la valeur du placeholder.
     */
    get placeholder(): string {
        return this._placeholder;
    }

    focused: boolean = true;

    /**
     * Retourne une valeur indiquant si le contrôle est vide.
     */
    get empty(): boolean {
        return this.value === null || this.value === 0;
    }

    /**
     * Retourne une valeur indiquant si le libellé doit être flottant.
     */
    @HostBinding('class.floated')
    get shouldLabelFloat(): boolean {
        // Le libellé est toujours 'floatant'.
        return true;
    }

    @Input()
    required: boolean;

    /**
     * Obtient une valeur indiquant si ce contrôle est désactivé.
     */
    @Input()
    disabled: boolean;

    /**
     * Retourne une valeur indiquant si le contrôle est en erreur.
     */
    get errorState() {
        return this.form.invalid;
    }

    controlType = 'mat-form-field-custom-control';

    //autofilled?: boolean;

    @Input('aria-describedby')
    userAriaDescribedBy?: string;

    @HostBinding('attr.aria.describedby')
    describedBy = '';

    disableAutomaticLabeling?: boolean;

    onChange: (value: number) => void;
    onTouch: () => void;

    constructor(
        private focusMonitor: FocusMonitor,
        private formBuilder: FormBuilder,
        private errorMatcher: ErrorStateMatcher,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl != null) {
            // Evite les erreurs d'injection de dépendance circulaire que l'on aurait si on utilisait un provider classique
            this.ngControl.valueAccessor = this;
        }

        this.form = this.formBuilder.group({
            timeInput: new FormControl('', [Validators.pattern(this.timePattern), Validators.required]),
        });
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const timeInput = this.form.get('timeInput').value as string;

        if (!timeInput || timeInput.length === 0) {
            return { required: true };
        }

        const match = this.timePattern.exec(timeInput);

        if (match) {
            return null;
        } else {
            return { format: true };
        }
    }

    ngOnInit() {
        // On s'abonne aux evenments liés au focus.
        this.focusMonitor.monitor(this.input).subscribe((focused) => {
            this.focused = !!focused;
            // Previent le formulaire que le composant a été modifié pour declencher le change detection.
            this.stateChanges.next();
        });

        this.focusMonitor
            .monitor(this.input)
            .pipe(take(1))
            .subscribe(() => {
                // Lorsque le composant obtient le focus la 1ere fois, on indique au formulaire que le composant a été touché (utile pour afficher les erreurs de validation).
                this.onTouch();
            });

        this.form.controls['timeInput'].valueChanges.subscribe((value) => {
            this.formatedTime = value;
            this.onChange(this.parseTimeInput(value));
            this.ngControl.control.updateValueAndValidity();
        });

        this.ngControl.control.setValidators([this.validate.bind(this)]);
        this.ngControl.control.updateValueAndValidity();
    }

    ngOnDestroy(): void {
        this.focusMonitor.stopMonitoring(this.input);
        this.stateChanges.complete();
    }

    setDescribedByIds(ids: string[]): void {
        this.describedBy = ids.join('');
    }

    onContainerClick(event: MouseEvent): void {
        this.focusMonitor.focusVia(this.input, 'program');
    }

    writeValue(obj: number): void {
        this.value = obj;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (isDisabled) {
            this.form.disable();
        } else {
            this.form.enable();
        }

        this.stateChanges.next();
    }

    parseTimeInput(expressionValue: string): number {
        if (!expressionValue || expressionValue.length === 0) {
            return 0;
        }

        const match = this.timePattern.exec(expressionValue);

        if (match) {
            const weeks = this.parseUnit(match[1], 's');
            const days = this.parseUnit(match[2], 'j');
            const hours = this.parseUnit(match[3], 'h');
            const minutes = this.parseUnit(match[4], 'm');

            const totalMinuts = weeks * 7 * 24 * 60 + days * 24 * 60 + hours * 60 + minutes;

            return totalMinuts;
        } else {
            return 0;
        }
    }
    private parseUnit(value: string | undefined, unit: string): number {
        if (!value) {
            return 0;
        }
        return parseInt(value.replace(unit, ''), 10);
    }

    formatTime(time: number): string {
        if (!time || time === 0) {
            return '';
        }

        const minuts = time % 60;
        const totalHours = Math.floor(time / 60);
        const remainingHours = totalHours % 24;
        const totalDays = Math.floor(totalHours / 24);
        const remainingDays = totalDays % 7;
        const weeks = Math.floor(totalDays / 7);

        let format: string = '';

        if (weeks > 0) {
            format += `${weeks}s`;
        }

        if (remainingDays > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingDays}j`;
        }

        if (remainingHours > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${remainingHours}h`;
        }

        if (minuts > 0) {
            if (format.length !== 0) {
                format += ' ';
            }
            format += `${minuts}m`;
        }

        return format;
    }
}
